// api.js - API utility functions using axios
import React from 'react';
import axios from './axios';

// Token management
export const tokenManager = {
  getAccessToken: () => localStorage.getItem('access_token'),
  getRefreshToken: () => localStorage.getItem('refresh_token'),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  },
  clearTokens: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },
  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  setUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
  }
};

// Setup axios interceptors for automatic token handling
const setupAxiosInterceptors = () => {
  // Request interceptor to add auth token
  axios.interceptors.request.use(
    (config) => {
      const token = tokenManager.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for token refresh
  // Response interceptor for token refresh
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = tokenManager.getRefreshToken();
          if (refreshToken) {
            const response = await axios.post('/auth/refresh', {}, {
              headers: { Authorization: `Bearer ${refreshToken}` }
            });

            const { access_token, refresh_token } = response.data;
            tokenManager.setTokens(access_token, refresh_token);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return axios(originalRequest);
          }
        } catch (refreshError) {
          // 🟦 Thay đổi logic redirect khi token refresh fail
          const user = tokenManager.getUser();
          tokenManager.clearTokens();

          if (user?.roles?.includes('teacher')) {
            window.location.href = '/teacher/login';
          } else {
            window.location.href = '/student/login';
          }

          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

};

// Initialize interceptors
setupAxiosInterceptors();

// Auth API functions
export const authAPI = {
  register: async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  },

  login: async (credentials) => {
    try {
      const response = await axios.post('/auth/login', credentials);
      const { accessToken, refreshToken, user } = response.data;
      tokenManager.setTokens(accessToken, refreshToken);
      tokenManager.setUser(user);

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  },

  logout: async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      const user = tokenManager.getUser();
      tokenManager.clearTokens();

      // 🟦 Điều hướng đúng trang login theo vai trò
      if (user?.roles?.includes('teacher')) {
        window.location.href = '/teacher/login';
      } else {
        window.location.href = '/student/login';
      }
    }
  },


  getCurrentUser: async () => {
    try {
      const response = await axios.get('/auth/me');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to get user info');
    }
  }
};

// Protected route HOC
export const withAuth = (WrappedComponent) => {
  return function AuthenticatedComponent(props) {
    const [isAuthenticated, setIsAuthenticated] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
      const checkAuth = async () => {
        const token = tokenManager.getAccessToken();

        if (!token) {
          setLoading(false);
          return;
        }

        try {
          await authAPI.getCurrentUser();
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth check failed:', error);
          tokenManager.clearTokens();
        }

        setLoading(false);
      };

      checkAuth();
    }, []);

    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      window.location.href = '/student/login';
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};

// Role-based access control
export const hasRole = (requiredRole) => {
  const user = tokenManager.getUser();
  if (!user || !user.roles) return false;

  return user.roles.includes(requiredRole);
};

export default {
  authAPI,
  tokenManager,
  withAuth,
  hasRole
};